import { dateToIso, isoToDate, todayIso, type IsoDate, type IsoDateRange } from './isoDate';

/**
 * One row of the quick-pick column beside the range calendar (Figma `40245:262685`).
 *
 * The LIST IS THE CALLER'S: which spans make sense depends on the screen — a filter wants
 * "last 6 months", a period editor wants financial years — so the field draws whatever it
 * is handed and highlights the row whose range equals the current value.
 */
export interface DateRangePreset {
  /** Stable identity for React and tests. */
  key: string;
  label: string;
  value: IsoDateRange;
}

const daysInMonth = (year: number, monthIndex: number): number =>
  new Date(year, monthIndex + 1, 0).getDate();

/**
 * The span of the last `months` months ending today, both ends inclusive:
 * on 07.10.2026, 12 months is 08.10.2025 – 07.10.2026.
 *
 * Month-end safe: going back from 31 March lands on the last day of the shorter month
 * rather than rolling into the next one.
 */
export function lastMonthsRange(months: number, today: IsoDate = todayIso()): IsoDateRange {
  const end = isoToDate(today) ?? new Date();
  const year = end.getFullYear();
  const targetMonth = end.getMonth() - months;
  const target = new Date(year, targetMonth, 1);
  const day = Math.min(end.getDate(), daysInMonth(target.getFullYear(), target.getMonth()));
  const start = new Date(target.getFullYear(), target.getMonth(), day + 1);
  return { start: dateToIso(start), end: dateToIso(end) };
}

/** 01.01 – 31.12 of one calendar year. */
export function calendarYearRange(year: number): IsoDateRange {
  return { start: `${year}-01-01`, end: `${year}-12-31` };
}

const shiftDay = (iso: IsoDate | null, years: number): IsoDate | null => {
  const date = isoToDate(iso);
  if (!date) return null;
  const year = date.getFullYear() + years;
  // 29.02 in a year without one is the 28th, not 1 March.
  const day = Math.min(date.getDate(), daysInMonth(year, date.getMonth()));
  return dateToIso(new Date(year, date.getMonth(), day));
};

/**
 * The same span `years` years later (negative: earlier) — a financial year's neighbours.
 * A span that ends on the last day of February keeps ending on it.
 */
export function shiftRangeByYears(range: IsoDateRange, years: number): IsoDateRange {
  const end = isoToDate(range.end);
  const endsFebruary =
    end !== null && end.getMonth() === 1 && end.getDate() === daysInMonth(end.getFullYear(), 1);
  const shiftedEnd = shiftDay(range.end, years);
  return {
    start: shiftDay(range.start, years),
    end:
      endsFebruary && end
        ? dateToIso(
            new Date(end.getFullYear() + years, 1, daysInMonth(end.getFullYear() + years, 1)),
          )
        : shiftedEnd,
  };
}
